import { Cross2Icon } from "@radix-ui/react-icons";
import { MessageResponse, OrgMessageRecipient } from "client/openapi";
import { PersonKind } from "client/openapi/models/PersonKind";
import PeoplePicker, {
  TaskPerson,
} from "components/Dashboard/ManageTasksDialog/PeoplePicker";
import PersonPill, { PillColor } from "components/PersonPill";
import { useState } from "react";

/** Someone an email reply goes to: a parent, student or tutor. */
export type EmailRecipient = TaskPerson;

type Person = { id: number; first_name: string; last_name: string };

/** The thread's colors for each kind of person. */
const COLORS: Record<string, PillColor> = {
  [PersonKind.PARENT]: "blue",
  [PersonKind.STUDENT]: "green",
  [PersonKind.TUTOR]: "gold",
};

const ROLES = [PersonKind.PARENT, PersonKind.STUDENT, PersonKind.TUTOR];

const recipientKey = (person: EmailRecipient) => `${person.role}:${person.id}`;

const asRecipient =
  (role: PersonKind) =>
  (person: Person): EmailRecipient => ({
    role,
    id: person.id,
    label: `${person.first_name} ${person.last_name}`,
  });

/**
 * Who a reply to an email goes to before anyone changes it: the people on
 * that email (a received one's sender and its To and Cc, or the people a
 * sent one went to). The server already leaves out the replying admin's own
 * records.
 */
export function answeredEmailPeople(
  message: MessageResponse
): EmailRecipient[] {
  return [
    ...(message.reply_parents ?? []).map(asRecipient(PersonKind.PARENT)),
    ...(message.reply_students ?? []).map(asRecipient(PersonKind.STUDENT)),
    ...(message.reply_tutors ?? []).map(asRecipient(PersonKind.TUTOR)),
  ];
}

/** The chosen people as POST /messages takes its recipients. */
export function toMessageRecipients(people: EmailRecipient[]) {
  const ids = (role: PersonKind) =>
    people.filter((person) => person.role === role).map((p) => Number(p.id));
  const students = ids(PersonKind.STUDENT);
  const tutors = ids(PersonKind.TUTOR);
  const parents = ids(PersonKind.PARENT);
  return {
    recipient_types: [
      ...(students.length ? [OrgMessageRecipient.STUDENTS] : []),
      ...(tutors.length ? [OrgMessageRecipient.TUTORS] : []),
      ...(parents.length ? [OrgMessageRecipient.PARENTS] : []),
    ],
    student_recipients: students,
    tutor_recipients: tutors,
    parent_recipients: parents,
  };
}

/**
 * The To line above an email reply: the people it goes to, as name pills.
 * × removes someone, and "+ Add" picks someone from the thread or the
 * organization's contacts. Sending needs at least one person, so with none
 * left the line says so.
 */
export default function EmailToLine({
  value,
  onChange,
  people,
  suggested = [],
}: {
  value: EmailRecipient[];
  onChange: (value: EmailRecipient[]) => void;
  /** Everyone who can be added: the organization's parents, students and tutors (see taskPeople). */
  people: EmailRecipient[];
  /** Listed first when adding: the people in the thread. */
  suggested?: EmailRecipient[];
}) {
  const [adding, setAdding] = useState(false);
  const chosen = new Set(value.map(recipientKey));

  return (
    <div className="flex flex-wrap items-center gap-1 text-[13px]">
      <span className="mr-1 text-gray-500">To</span>
      {value.map((person) => (
        <span
          key={recipientKey(person)}
          className="inline-flex min-w-0 max-w-[16rem] items-center gap-0.5"
        >
          <PersonPill label={person.label} color={COLORS[person.role]} />
          <button
            type="button"
            className="rounded-full p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={`Remove ${person.label}`}
            title={`Remove ${person.label}`}
            onClick={() =>
              onChange(
                value.filter((p) => recipientKey(p) !== recipientKey(person))
              )
            }
          >
            <Cross2Icon width="10" height="10" />
          </button>
        </span>
      ))}
      {adding ? (
        <div
          className="min-w-[14rem]"
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              setAdding(false);
            }
          }}
        >
          <PeoplePicker
            inputId="email-reply-add"
            people={people.filter((p) => !chosen.has(recipientKey(p)))}
            roles={ROLES}
            suggested={suggested}
            value={[]}
            onChange={(picked) => {
              onChange([...value, ...picked]);
              setAdding(false);
            }}
            placeholder="Add someone"
            autoFocus
          />
        </div>
      ) : (
        <button
          type="button"
          className="rounded px-1 text-[12px] font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          onClick={() => setAdding(true)}
        >
          + Add
        </button>
      )}
      {value.length === 0 && !adding && (
        <span className="text-[12px] text-gray-400">Add someone to send</span>
      )}
    </div>
  );
}
